import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { toast } from "react-hot-toast";
import "./edit-service-form.scss";
import { SingleCard } from "../../layouts";
import { useAuth } from "../../contexts/auth";
import { baseAxios } from "../../utils/config";
import { ServiceForm } from "../create-service-form/service-form";

export default function EditServiceForm(props) {
  const {
    serviceId: serviceIdProp,
    onSaved,
    onRequestClose,
    onLoadingChange,
    formId = "edit-service-form",
  } = props;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [service, setService] = useState([]);
  const formData = useRef({ taxesIncluded: false });
  const [formRenderKey, setFormRenderKey] = useState(0);

  const getServiceById = "gateway/2m2zym0pu2";

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const serviceId =
    serviceIdProp != null && serviceIdProp !== ""
      ? String(serviceIdProp)
      : queryParams.get("serviceId");

  const embedded =
    typeof onSaved === "function" || typeof onRequestClose === "function";

  const userCredentials = {
    Barcode: user.barcode,
    Role: user.userRoles,
    Organization: user.org,
    Location: user.location_id,
  };

  const toBoolean = (value) => value === "1" || value === true;

  useEffect(() => {
    onLoadingChange?.(loading || fetching);
  }, [loading, fetching, onLoadingChange]);

  useEffect(() => {
    document.body.style.cursor = loading ? "progress" : "default";
    return () => {
      document.body.style.cursor = "default";
    };
  }, [loading]);

  useEffect(() => {
    if (!serviceId) return;

    let cancelled = false;
    setFetching(true);

    const fetchServiceById = async () => {
      const payload = {
        serviceId: serviceId,
        role: userCredentials.Role,
        organization: userCredentials.Organization,
      };
      try {
        const response = await baseAxios.post(getServiceById, payload);
        if (cancelled) return;

        const serviceData = response.data[0];

        if (serviceData) {
          serviceData.taxes_included = toBoolean(serviceData.taxes_included);
          setService(serviceData);
          formData.current = {
            ...formData.current,
            name: serviceData.service_name,
            shortcutName: serviceData.service_shortcut,
            description: serviceData.description,
            status: serviceData.service_status,
            serviceCategory: Number(serviceData.category_id),
            freeService:
              !serviceData.service_price_level_1 &&
              !serviceData.service_price_level_2 &&
              !serviceData.service_price_level_3 &&
              !serviceData.service_price_level_4 &&
              !serviceData.service_price_level_5,
            price1: serviceData.service_price_level_1 ?? 0,
            price2: serviceData.service_price_level_2 ?? 0,
            price3: serviceData.service_price_level_3 ?? 0,
            price4: serviceData.service_price_level_4 ?? 0,
            price5: serviceData.service_price_level_5 ?? 0,
            taxesIncluded: serviceData.taxes_included,
          };
          setFormRenderKey((prevKey) => prevKey + 1);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch service details", error);
          toast.error("Failed to load service details", { duration: 3000 });
        }
      } finally {
        if (!cancelled) setFetching(false);
      }
    };

    fetchServiceById();

    return () => {
      cancelled = true;
    };
  }, [serviceId]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (
      !userCredentials.Role.includes(6) &&
      !userCredentials.Role.includes(3) &&
      !userCredentials.Role.includes(2)
    ) {
      toast.error("User Not authorized, please check your role", {
        duration: 5000,
      });
      setLoading(false);
      return;
    }

    if (
      !formData.current.freeService &&
      !formData.current.price1 &&
      !formData.current.price2 &&
      !formData.current.price3 &&
      !formData.current.price4 &&
      !formData.current.price5
    ) {
      toast.error(
        "At least one price level must be set if the service has a cost.",
        { duration: 3000 }
      );
      setLoading(false);
      return;
    }

    try {
      const response = await baseAxios.post(
        `/services/editService/${service.service_id}`,
        formData.current
      );

      if (response.status === 200) {
        toast.success("Service Updated", { duration: 3000 });
        if (typeof onSaved === "function") {
          onSaved();
        } else {
          setTimeout(() => {
            navigate("/search-services");
          }, 3000);
        }
      }
      setLoading(false);
    } catch (e) {
      toast.error("Service update failed, please try again", { duration: 5000 });
      setLoading(false);
    }
  };

  const formBody = fetching ? (
    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
      <CircularProgress size={28} color="primary" />
    </Box>
  ) : (
    <form
      id={formId}
      className="edit-service-form"
      onSubmit={onSubmit}
      style={{ cursor: loading ? "progress" : "default" }}
    >
      <ServiceForm
        key={formRenderKey}
        loading={loading}
        formData={formData}
        isEdit={true}
        embedded={embedded}
      />
    </form>
  );

  if (embedded) {
    return formBody;
  }

  return (
    <div style={{ cursor: loading ? "progress" : "default" }}>
      <SingleCard title="Edit Service">{formBody}</SingleCard>
    </div>
  );
}
